import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import Form, { ButtonItem, SimpleItem, EmptyItem, GroupItem, ColCountByScreen, Label } from 'devextreme-react/form'
import 'devextreme-react/tag-box'
import dxTextArea from 'devextreme/ui/text_area'
import JumboCardQuick from '@jumbo/components/JumboCardQuick'
import Typography from '@mui/material/Typography'
import util from 'app/utils/components/getYears/getNextFiveYear'
import { useSelector } from 'react-redux'
import termServices from '../../../../../../services/pages/courses/setup/termServices/termServices'
import DataSource from 'devextreme/data/data_source'
import CustomStore from 'devextreme/data/custom_store'
import utilServices from '../../../../../../services/util-services'
import { useSnackbar } from 'notistack'
import { IconButton, Stack, useMediaQuery } from '@mui/material'
import { termStartEndDate } from 'app/services/utils/components/termStartEndDate/termStartEndDate'
import WestIcon from '@mui/icons-material/West'

const AddTermForm = (props) => {
    const navigate = useNavigate()
    const { handleSubmit } = useForm()
    const { loading } = useSelector((state) => state.terms)
    const { enqueueSnackbar } = useSnackbar()
    const [labelLocation, setLabelLocation] = useState('left')
    const sm = useMediaQuery('(max-width:600px)')

    useEffect(() => {
        setLabelLocation(sm ? 'top' : 'left')
    }, [sm])

    const termYearEditorOptions = {
        dataSource: util.getNextFiveYear(),
    }

    function handleError(error) {
        if (error.status === '401') {
            navigate('/profile/signout')
        } else if (error.detail) {
            enqueueSnackbar(error.detail, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        } else {
            enqueueSnackbar(error, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        }
    }

    const termLookupList = useCallback(async (searchTerm) => {
        try {
            return await termServices.getTermLookupList({ termYear: searchTerm, past: true, future: false })
        } catch (error) {
            handleError(error)
        }
    }, [])

    const termLookupById = useCallback(async (key) => {
        try {
            return await termServices.getTermLookupById(key)
        } catch (error) {
            handleError(error)
        }
    }, [])

    const termLookupsStore = useMemo(() => {
        return new DataSource({
            store: new CustomStore({
                key: 'id',
                load: async function (loadOptions) {
                    const searchTerm = loadOptions.searchValue || ''
                    return await termLookupList(searchTerm)
                },
                byKey: async function (key) {
                    if (key === '') return utilServices.emptyList()
                    return await termLookupById(key)
                },
                errorHandler: function (e) {
                    console.error(e)
                    return utilServices.emptyList()
                },
            }),
            reshapeOnPush: true,
        })
    }, [])

    const termEditorOptions = {
        dataSource: termLookupsStore,
        displayExpr: termStartEndDate,
        valueExpr: 'id',
        searchEnabled: true,
        showClearButton: true,
        itemTemplate: termStartEndDate,
    }

    const submitButtonOptions = {
        text: 'Save',
        type: 'default',
        width: 120,
        useSubmitBehavior: true,
    }

    const cancelButtonOptions = {
        text: 'Cancel',
        type: 'Normal',
        width: 120,
        onClick: () =>
            navigate('/courses/setup/terms', {
                state: { navigateFrom: 'cancel-add-term' },
            }),
    }

    const validationRules = {
        termYear: [{ type: 'required', message: 'Term year is required.' }],
        termName: [{ type: 'required', message: 'Term Name is required.' }],
        dateStart: [{ type: 'required', message: 'Date Start is required.' }],
        dateEnd: [{ type: 'required', message: 'Date End is required.' }],
        //prvTermId: [{ type: 'required', message: 'Previous Term is required.' }],
    }

    return (
        <React.Fragment>
            <JumboCardQuick
                title={
                    <Stack direction='row' alignItems='center' spacing={1}>
                        <IconButton
                            className='backArrowForm'
                            onClick={() =>
                                navigate(`/courses/setup/terms`, {
                                    state: { navigateFrom: 'cancel-add-term' },
                                })
                            }>
                            <WestIcon />
                        </IconButton>
                        <Typography color={'common.white'} variant={'h3'} sx={{ mt: 0.8 }}>
                            Add Term
                        </Typography>
                    </Stack>
                }
                headerSx={{
                    height: 40,
                    bgcolor: 'primary.main',
                }}>
                <form onSubmit={handleSubmit(props.addTerm)}>
                    <Form
                        mode='form'
                        formData={props.termData}
                        onFieldDataChanged={props.onFieldDataChanged}
                        showColonAfterLabel={false}
                        showValidationSummary={false}
                        labelLocation={labelLocation}>
                        <GroupItem cssClass='form-group-item' caption='New Term'>
                            <ColCountByScreen lg={13} md={13} sm={1} xs={1} />
                            <GroupItem />
                            <GroupItem />
                            <GroupItem />
                            <GroupItem cssClass='form-group-item' colSpan={6}>
                                <EmptyItem />
                                <SimpleItem
                                    dataField='termYear'
                                    editorType='dxSelectBox'
                                    editorOptions={termYearEditorOptions}
                                    validationRules={validationRules.termYear}
                                />
                                <SimpleItem
                                    dataField='termNumber'
                                    editorType='dxNumberBox'
                                    editorOptions={{ showSpinButtons: 'true' }}>
                                    <Label text='Term Number' />
                                </SimpleItem>
                                <SimpleItem dataField='termName' validationRules={validationRules.termName} />
                                <SimpleItem dataField='comments' editorType={dxTextArea} />
                                <SimpleItem
                                    dataField='dateStart'
                                    editorType='dxDateBox'
                                    validationRules={validationRules.dateStart}
                                    editorOptions={{
                                        displayFormat: 'shortDate',
                                        type: 'date',
                                        pickerType: 'calendar',
                                        useMaskBehavior: true,
                                    }}
                                />
                                <SimpleItem
                                    dataField='dateEnd'
                                    editorType='dxDateBox'
                                    validationRules={validationRules.dateEnd}
                                    editorOptions={{
                                        displayFormat: 'shortDate',
                                        type: 'date',
                                        pickerType: 'calendar',
                                        useMaskBehavior: true,
                                    }}
                                />
                                <SimpleItem
                                    dataField='prvTermId'
                                    editorType='dxSelectBox'
                                    //validationRules={validationRules.prvTermId}
                                    editorOptions={termEditorOptions}>
                                    <Label text='Previous Term' />
                                </SimpleItem>
                            </GroupItem>
                        </GroupItem>
                        <EmptyItem />
                        <GroupItem>
                            <ColCountByScreen lg={2} md={2} sm={2} xs={2} />
                            <ButtonItem
                                buttonOptions={{
                                    disabled: loading,
                                    icon: loading ? 'refresh' : 'check',
                                    ...submitButtonOptions,
                                }}
                                horizontalAlignment='right'
                            />
                            <ButtonItem buttonOptions={cancelButtonOptions} horizontalAlignment='left' />
                        </GroupItem>
                    </Form>
                </form>
            </JumboCardQuick>
        </React.Fragment>
    )
}

export default AddTermForm
